import React from "react";
import { Building2, ImagePlus, Globe, Inbox, FileDown, FileUp, ShieldCheck, Server } from "lucide-react";

const Block = ({ icon: Icon, title, children, testId }) => (
  <section className="border border-ivory/10 bg-[#242424] p-6 sm:p-8" data-testid={testId}>
    <h2 className="flex items-center gap-3 font-serif text-2xl text-ivory"><Icon className="h-5 w-5 text-ivory/60" /> {title}</h2>
    <div className="mt-4 space-y-3 text-sm leading-relaxed text-ivory/70">{children}</div>
  </section>
);

const Li = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rotate-45 bg-ivory/40" />
    <span>{children}</span>
  </li>
);

export default function AdminGuide() {
  return (
    <div data-testid="admin-guide">
      <h1 className="font-serif text-3xl text-ivory sm:text-4xl">User Guide</h1>
      <p className="mt-2 max-w-2xl text-sm text-ivory/60">Everything you need to manage the property listings, in plain language. Bookmark this page.</p>

      <div className="mt-8 space-y-6">
        <Block icon={Building2} title="Adding a property" testId="guide-add">
          <ul className="space-y-2">
            <Li>Click <strong className="text-ivory">+ Add New Property</strong> from the Dashboard or Properties page.</Li>
            <Li>Fill in the basic information. Only the title, district, location and price are required.</Li>
            <Li>Add photos in the Photos section — drag them in, or tap to choose from your phone.</Li>
            <Li>Click <strong className="text-ivory">Save draft</strong> to keep it private, or <strong className="text-ivory">Publish to website</strong> to make it live immediately.</Li>
          </ul>
        </Block>

        <Block icon={ImagePlus} title="Managing photos" testId="guide-photos">
          <ul className="space-y-2">
            <Li>The photo marked <strong className="text-ivory">Cover</strong> appears first on the website — tap the star to choose it.</Li>
            <Li>Use the left/right arrows on a photo to change the order.</Li>
            <Li>Photos are automatically optimised and stored in secure cloud storage, so they stay online even when the website is redeployed.</Li>
          </ul>
        </Block>

        <Block icon={Globe} title="What visitors see" testId="guide-public">
          <ul className="space-y-2">
            <Li>Only <strong className="text-ivory">published</strong> properties appear on the public website.</Li>
            <Li>Setting a property to <strong className="text-ivory">Sold</strong> or <strong className="text-ivory">Under Offer</strong> keeps it visible with that label.</Li>
            <Li><strong className="text-ivory">Archive</strong> hides a property completely; you can restore it later from the Archived filter.</Li>
            <Li>Every change you save shows on the website straight away — there is nothing else to press.</Li>
          </ul>
        </Block>

        <Block icon={Inbox} title="Handling enquiries" testId="guide-enquiries">
          <ul className="space-y-2">
            <Li>New website enquiries appear under <strong className="text-ivory">Enquiries</strong> with the person's name, contact details and message.</Li>
            <Li>Mark each one <strong className="text-ivory">Contacted</strong> or <strong className="text-ivory">Closed</strong> so Andy always knows what's outstanding.</Li>
            <Li>WhatsApp and phone enquiries still come to Andy directly as usual — this page does not replace them.</Li>
          </ul>
        </Block>

        <Block icon={FileUp} title="Moving listings from the old site" testId="guide-import">
          <ul className="space-y-2">
            <Li>Open <strong className="text-ivory">Import CSV</strong>, download the template and fill in one row per property.</Li>
            <Li>Upload the finished file and check the preview — anything with a problem is clearly marked and skipped.</Li>
            <Li>Imported listings arrive as drafts so Andy can review them before publishing.</Li>
          </ul>
        </Block>

        <Block icon={FileDown} title="Backups &amp; your data" testId="guide-backup">
          <ul className="space-y-2">
            <Li>Click <strong className="text-ivory">Export CSV</strong> on the Properties page anytime to download every listing as a spreadsheet — keep a copy monthly.</Li>
            <Li>Listing records live in a dedicated database and photos in separate cloud storage; neither is lost when the website is updated or redeployed.</Li>
            <Li>Recommended habit: export the CSV on the first of each month and save it to Andy's shared drive.</Li>
          </ul>
        </Block>

        <Block icon={ShieldCheck} title="Handover checklist (for Andy)" testId="guide-handover">
          <ul className="space-y-2">
            <Li>Owner sign-in email and password are recorded in your password manager.</Li>
            <Li>Your PA has their own Property Manager account — never share the owner password.</Li>
            <Li>You know how to revoke PA access (Team &amp; Access → Revoke) if someone leaves.</Li>
            <Li>A monthly CSV export habit is in place.</Li>
            <Li>Photos belong to listings in cloud storage and can be re-linked to any future website.</Li>
          </ul>
        </Block>

        <Block icon={Server} title="How it's hosted (plain English)" testId="guide-hosting">
          <ul className="space-y-2">
            <Li>The website runs on a managed cloud platform; listings sit in a MongoDB database and photos in object storage.</Li>
            <Li>These two data stores are independent of the website code, so redesigns and updates never touch your data.</Li>
            <Li>If you ever move providers, the CSV export plus your stored photos are everything needed to rebuild the catalogue elsewhere.</Li>
          </ul>
        </Block>
      </div>
    </div>
  );
}
